import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  MCPActivityListParams,
  SchoolAPIKeyCreateInput,
  SchoolAPIKeyListParams,
} from './types';

/** Query-key factory — every api-keys key derives from here. */
export const apiKeyKeys = {
  all: ['apiKeys'] as const,
  keyLists: () => [...apiKeyKeys.all, 'keys'] as const,
  keyList: (params: SchoolAPIKeyListParams) => [...apiKeyKeys.keyLists(), params] as const,
  activityLists: () => [...apiKeyKeys.all, 'activity'] as const,
  activityList: (params: MCPActivityListParams) => [...apiKeyKeys.activityLists(), params] as const,
};

export function useApiKeys(params: SchoolAPIKeyListParams = {}) {
  return useQuery({
    queryKey: apiKeyKeys.keyList(params),
    queryFn: () => api.getApiKeys(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Mint a key. The plaintext lives ONLY in this mutation's result — it is never
 * written to the query cache, because a cache is a place a secret can outlive
 * the dialog that showed it.
 */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SchoolAPIKeyCreateInput) => api.createApiKey(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: apiKeyKeys.keyLists() }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefix: string) => api.revokeApiKey(prefix),
    onSuccess: () => void qc.invalidateQueries({ queryKey: apiKeyKeys.keyLists() }),
  });
}

/** The audit surface for AI-created content. Role scoping is the server's. */
export function useMcpActivity(params: MCPActivityListParams = {}) {
  return useQuery({
    queryKey: apiKeyKeys.activityList(params),
    queryFn: () => api.getMcpActivity(params),
    placeholderData: keepPreviousData,
  });
}
